import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';

/**
 * Client-side pagination over an already-fetched row array.
 *
 * Every list in paceBI arrives whole (the daemon caps at the query level —
 * 5000 tickets, 500 AR rows, the full nightly SKU snapshot, …) so slicing on
 * the client is the right fit: no extra round-trips to a single-threaded
 * daemon, and CSV/PDF exports keep the full set. The hook only owns the
 * page cursor; callers render `pageRows` and hand the rest to <Pagination>.
 *
 * Contract for callers: pass a *stable* array (state, or a useMemo'd
 * derivation). The cursor resets to page 1 whenever the array identity
 * changes — that is what makes "change a filter → back to the first page"
 * work — so an array rebuilt on every render would pin the user to page 1.
 */
export const PAGE_SIZES = [25, 50, 100, 250] as const;
export const DEFAULT_PAGE_SIZE = 50;
/** One preference for the whole app — a user who likes 100-row pages wants it everywhere. */
export const PAGE_SIZE_KEY = 'pacebi-page-size';

export interface PaginationState<T> {
  /** 1-based, always within [1, pageCount]. */
  page: number;
  pageCount: number;
  pageSize: number;
  total: number;
  /** 1-based row numbers of the visible slice; both 0 when there are no rows. */
  from: number;
  to: number;
  /** The rows to render. Every row while the page is printing. */
  pageRows: T[];
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
}

const EMPTY: never[] = [];

function readStoredPageSize(): number {
  try {
    const n = Number(localStorage.getItem(PAGE_SIZE_KEY));
    if ((PAGE_SIZES as readonly number[]).includes(n)) return n;
  } catch { /* storage blocked (private mode) — default below */ }
  return DEFAULT_PAGE_SIZE;
}

export function usePagination<T>(rows: readonly T[] | null | undefined): PaginationState<T> {
  const list: readonly T[] = rows ?? EMPTY;
  const [pageSize, setPageSizeState] = useState<number>(readStoredPageSize);
  const [page, setPageState] = useState(1);
  const [printing, setPrinting] = useState(false);

  // Reset the cursor when the row set is replaced. A layout effect (not a
  // render-phase setState) so that a caller who violates the stable-array
  // contract degrades to "stuck on page 1" instead of an infinite re-render
  // crash; useLayoutEffect rather than useEffect so the clamped old page
  // never paints against the new rows.
  useLayoutEffect(() => { setPageState(1); }, [list]);

  // Browsers fire beforeprint/afterprint around the print dialog; expose the
  // whole list in between so a printed report isn't cut at the page boundary.
  useEffect(() => {
    const on = () => setPrinting(true);
    const off = () => setPrinting(false);
    window.addEventListener('beforeprint', on);
    window.addEventListener('afterprint', off);
    return () => {
      window.removeEventListener('beforeprint', on);
      window.removeEventListener('afterprint', off);
    };
  }, []);

  const total = list.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const current = Math.min(Math.max(1, page), pageCount);
  const start = (current - 1) * pageSize;
  const end = Math.min(start + pageSize, total);

  const pageRows = useMemo<T[]>(
    () => (printing ? list.slice() : list.slice(start, end)),
    [list, start, end, printing],
  );

  const setPage = useCallback(
    (p: number) => setPageState(Math.min(Math.max(1, Math.trunc(p) || 1), pageCount)),
    [pageCount],
  );

  const setPageSize = useCallback((size: number) => {
    const n = Math.trunc(size);
    if (!Number.isFinite(n) || n <= 0) return;
    setPageSizeState(n);
    setPageState(1);
    try { localStorage.setItem(PAGE_SIZE_KEY, String(n)); } catch { /* best-effort */ }
  }, []);

  return {
    page: current,
    pageCount,
    pageSize,
    total,
    from: total === 0 ? 0 : start + 1,
    to: total === 0 ? 0 : end,
    pageRows,
    setPage,
    setPageSize,
  };
}
